import ResponsiveEditDialog from "@/modules/common/components/modals/responsive-edit-dialog";
import { UpdateLeadTypeRequestV2 } from "@/modules/lead-page/dtos/index-v2";
import useEditLeadTypeAndReferral from "@/modules/lead-page/hooks/lead-info/edit-lead-type-referral.hook";

import { UserTag } from "@/modules/user";
import {
  Autocomplete,
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { SyntheticEvent, useContext, useMemo, useState } from "react";
import { DynamicForm } from "@/modules/common/components/dynamic-form/dynamic-form";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";
import {
  AssignmentMethods,
  LeadReferralOptions,
  LeadReferralOptionsType,
  LeadTypeOption,
  LeadTypeRadioAssignmentType,
} from "@/modules/lead-page/constants";
import { mapReferralValues } from "@/modules/common/utils";
import { LookupsOptions } from "@/modules/common/dtos/index.v2";
import { UserV2 } from "@/modules/user/dtos/index.v2";
const EditLeadType: React.FC<{
  optionId: LeadTypeOption;
  setModalOpen: (open: boolean) => void;
  modalOpen: boolean;
  setReferralOption?: (option: LeadReferralOptionsType) => void;
  referralOption?: LeadReferralOptionsType;
}> = ({
  optionId,
  setModalOpen,
  modalOpen,
  setReferralOption,
  referralOption,
}) => {
  const { lead } = useContext(LeadPageContext);
  const [selectedType, setSelectedType] = useState<LookupsOptions | null>(null);
  const [selectedDefaultUser, setDefaultUser] = useState<{
    id: string;
    name: string;
    color: string;
  } | null>(null);
  const [userId, setuserId] = useState<string>("");

  const {
    leadTypes,
    leadTypeMutation,
    setRadioValue,
    radioValue,
    leadTypeFormFields,
    usersByLeadType,
  } = useEditLeadTypeAndReferral({
    setModalOpen,
    setSelectedType,
    selectedType,
  });
  const filteredLeadTypes = useMemo(() => {
    return (leadTypes.data || []).filter(
      (type) => type.uid !== lead?.data?.lead?.lead_type?.uid
    );
  }, [leadTypes.data, lead?.data?.lead?.lead_type?.uid]);

  const userOptions = useMemo(() => {
    return (
      usersByLeadType?.data?.map((user: UserV2) => ({
        name: user?.name ?? "",
        id: user?.uid ?? "",
        color: user?.color ?? "",
      })) ?? []
    );
  }, [usersByLeadType?.data]);

  const handleChange = (_: SyntheticEvent, value: LookupsOptions | null) => {
    if (value) {
      setSelectedType(value);
    }
  };
  const handleDefaultUser = (
    _: SyntheticEvent,
    value: { name: string; id: string; color: string } | null
  ) => {
    if (value) {
      setDefaultUser(value);
      setuserId(value.id);
    }
  };

  const handleRadioChange = (
    _: SyntheticEvent,
    value: LeadTypeRadioAssignmentType
  ) => {
    setRadioValue(value);
  };

  const handleReferralOptionChange = (
    _: SyntheticEvent,
    value: LeadReferralOptionsType
  ) => {
    if (setReferralOption) {
      setReferralOption(value);
    }
  };
  const handleSubmitLeadType = (values?: { [key: string]: any }) => {
    if (selectedType) {
      let referralData: UpdateLeadTypeRequestV2 = {
        leadTypeUId: selectedType.uid,
      };
      if (values) {
        referralData = {
          ...referralData,
          ...mapReferralValues(values),
        };
      }

      const data: UpdateLeadTypeRequestV2 = {
        ...referralData,
        ...(selectedDefaultUser
          ? { newUserUId: userId }
          : optionId === LeadTypeOption.ReassignNoReferral ||
            optionId === LeadTypeOption.ReassignReferral
          ? { autoAssign: true }
          : {}),
        ...(optionId === LeadTypeOption.ReassignReferral && {
          ...(referralOption ===
            LeadReferralOptions.KEEP_CURRENT_LEAD_AND_REFER_NEW_LEAD && {
            keepCurrentLead: true,
          }),
          referralCommissionApplies: true,
        }),
      };

      leadTypeMutation.mutate(data);
    }

    setDefaultUser(null);
    setRadioValue(AssignmentMethods.AUTO_ASSIGN);
  };

  const renderContent = () => (
    <Box sx={{ width: "100%" }}>
      {optionId === LeadTypeOption.ReassignReferral && (
        <FormControl>
          <RadioGroup
            aria-labelledby="radio-buttons-group-label"
            name="radio-buttons-group"
            value={referralOption}
            onChange={(_: SyntheticEvent, value: string) => {
              handleReferralOptionChange(_, value as LeadReferralOptionsType);
            }}
          >
            <FormControlLabel
              value={LeadReferralOptions.KEEP_CURRENT_LEAD_AND_REFER_NEW_LEAD}
              control={<Radio />}
              label={LeadReferralOptions.KEEP_CURRENT_LEAD_AND_REFER_NEW_LEAD}
            />
            <FormControlLabel
              value={LeadReferralOptions.CHANGE_CURRENT_LEAD_AND_REFER}
              control={<Radio />}
              label={LeadReferralOptions.CHANGE_CURRENT_LEAD_AND_REFER}
            />
          </RadioGroup>
        </FormControl>
      )}
      <Autocomplete
        id="combo-box-demo"
        fullWidth
        options={filteredLeadTypes}
        value={selectedType}
        onInputChange={(_: SyntheticEvent, value: string) => {
          leadTypes.setSearchTerm(value);
        }}
        loading={leadTypes.isLoading}
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.name
        }
        isOptionEqualToValue={(option, value) => {
          if (typeof option !== "string" && typeof value !== "string") {
            return option.uid === value?.uid;
          }
          return option === value;
        }}
        onChange={(_: SyntheticEvent, value) => {
          if (typeof value !== "string") {
            handleChange(_, value!);
          } else {
            handleChange(_, null);
          }
        }}
        sx={{
          width: "100%",
          marginTop: "20px",
          "&:placeholder": {
            color: "#848E9C",
          },
        }}
        renderInput={(params) => <TextField {...params} label="Lead Type" />}
      />
      {optionId !== LeadTypeOption.TypeCorrection && (
        <>
          <FormControl
            sx={{
              marginTop: "20px",
              opacity: selectedType ? 1 : 0.5,
              pointerEvents: selectedType ? "auto" : "none",
            }}
          >
            <FormLabel id="radio-buttons-group-label"></FormLabel>
            <RadioGroup
              aria-labelledby="radio-buttons-group-label"
              name="radio-buttons-group"
              value={radioValue}
              onChange={(_: SyntheticEvent, value: string) => {
                handleRadioChange(_, value as LeadTypeRadioAssignmentType);
              }}
            >
              <FormControlLabel
                value={AssignmentMethods.AUTO_ASSIGN}
                control={<Radio />}
                label={AssignmentMethods.AUTO_ASSIGN}
              />
              <FormControlLabel
                value={AssignmentMethods.SELECT_USER}
                control={<Radio />}
                label={AssignmentMethods.SELECT_USER}
              />
            </RadioGroup>
          </FormControl>

          <Autocomplete
            id="combo-box-user"
            fullWidth
            options={userOptions}
            getOptionLabel={(option) => option.name || ""}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            onChange={(_: SyntheticEvent, value) => {
              handleDefaultUser(_, value!);
            }}
            loading={usersByLeadType.isLoading}
            sx={{
              width: "100%",
              marginTop: "10px",
              opacity: radioValue === AssignmentMethods.SELECT_USER ? 1 : 0.5,
              pointerEvents:
                radioValue === AssignmentMethods.SELECT_USER ? "auto" : "none",
              "&:placeholder": {
                color: "#848E9C",
              },
            }}
            value={selectedDefaultUser}
            defaultValue={selectedDefaultUser}
            renderOption={(props, option) => (
              <li {...props}>
                <UserTag name={option.name} color={option.color} />
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search by name"
                InputProps={{
                  ...params.InputProps,
                }}
              />
            )}
          />
        </>
      )}
      {optionId === LeadTypeOption.ReassignReferral &&
        leadTypeFormFields.data && (
          <DynamicForm
            handleConfirm={handleSubmitLeadType}
            isLoadingSubmition={leadTypeMutation.isLoading}
            disabledConfirmButton={
              radioValue === AssignmentMethods.SELECT_USER
                ? selectedType && selectedDefaultUser
                  ? false
                  : true
                : false
            }
            handleClose={() => setModalOpen(false)}
            formFields={leadTypeFormFields.data}
          />
        )}
    </Box>
  );
  return (
    <ResponsiveEditDialog
      open={modalOpen}
      setOpen={setModalOpen}
      isLoading={leadTypeMutation.isLoading}
      width={600}
      header={
        optionId === LeadTypeOption.TypeCorrection
          ? "Change lead type"
          : optionId === LeadTypeOption.ReassignNoReferral
          ? "Reassign to a different team (no referral)"
          : "Refer Lead (Commission Applies)"
      }
      handleConfirm={() => handleSubmitLeadType({})}
      hideModalFooter={
        optionId === LeadTypeOption.ReassignReferral && leadTypeFormFields.data
          ? true
          : false
      }
      disabledConfirmButton={
        radioValue === "Select a user"
          ? selectedType && selectedDefaultUser
            ? false
            : true
          : selectedType
          ? false
          : true
      }
      modalFooter={
        optionId === LeadTypeOption.ReassignReferral && leadTypeFormFields.data
          ? false
          : true
      }
      handleCloseModal={() => setSelectedType(null)}
      confirmText="Submit"
      enableEnterKeySubmit={false}
    >
      {renderContent()}
    </ResponsiveEditDialog>
  );
};

export default EditLeadType;
